import { Fragment } from "react";
import { useNavigate } from "@tanstack/react-router";
import { Badge, Box, Flex, HStack, Icon, Table, Text } from "@chakra-ui/react";
import { statusPresentation } from "./activityStatus";
import { Tooltip } from "./ui/tooltip";

export type ProgressRow = {
  personActivityId: string;
  title: string;
  status: string;
  statusLabel: string;
  dueDay: string | null;
  required: boolean;
  origin: string;
  overdue: boolean;
  stepsDone: number;
  stepsTotal: number;
  /** A whole phrase — "your review", "supervisor to fill in" — or null. */
  waitingOn: string | null;
  /** Whether that phrase is about the viewer, so a count can point at it. */
  waitingOnViewer: boolean;
  /** What it is waiting for, each with the id to reach it by. */
  blockedBy: Array<{ title: string; personActivityId: string | null }>;
  allStepsDone: boolean;
};

/**
 * What a hovered count is counting.
 *
 * The badges above a table say "2 late" and "1 waiting on you"; pointing at
 * one should show which rows it means, because a number without its members
 * makes the reader search for them by hand.
 */
export type RowHighlight = "late" | "blocked" | "waitingOnViewer" | null;

function matchesHighlight(row: ProgressRow, highlight: RowHighlight): boolean {
  switch (highlight) {
    case "late":
      return row.overdue;
    case "blocked":
      return row.status === "blocked";
    case "waitingOnViewer":
      return row.waitingOnViewer;
    default:
      return false;
  }
}

/**
 * The status as a sentence, with any activity it names set apart.
 *
 * Nodes rather than a string, because the names inside it need emphasis: the
 * whole row is the link here, so they cannot be links of their own, and an
 * unmarked title disappears into the sentence around it.
 */
function StatusSentence({ row }: { row: ProgressRow }) {
  if (row.status !== "blocked") {
    return <>{row.statusLabel}</>;
  }
  if (row.blockedBy.length === 0) {
    return <>Not yet open</>;
  }

  const names = row.blockedBy.map((entry, index) => (
    <Fragment key={`${entry.title}-${index}`}>
      {index > 0 ? " and " : ""}
      <Text as="span" fontWeight="semibold" color="fg">{entry.title}</Text>
    </Fragment>
  ));

  return row.allStepsDone
    ? <>All steps done — waiting on {names}</>
    : <>Waiting on {names}</>;
}

/** The plain-text form, for an accessible name where nodes will not do. */
function statusSentenceText(row: ProgressRow): string {
  if (row.status !== "blocked") {
    return row.statusLabel;
  }
  if (row.blockedBy.length === 0) {
    return "Not yet open";
  }
  const waiting = row.blockedBy.map((entry) => entry.title).join(" and ");
  return row.allStepsDone
    ? `All steps done — waiting on ${waiting}`
    : `Waiting on ${waiting}`;
}

function formatDay(day: string | null) {
  if (day === null) {
    return "—";
  }
  return new Intl.DateTimeFormat("en-GB", { day: "2-digit", month: "short", year: "2-digit" })
    .format(new Date(`${day}T12:00:00Z`));
}

/**
 * The activities of one year, or of one student, as compact rows.
 *
 * A table rather than a stack of cards: these are read by scanning down one
 * column at a time — what is late, what is mine — and cards put every row in
 * its own box, which is what makes a long list unscannable.
 *
 * The whole row navigates. A link around the title alone gives a two-word
 * target in a row that is otherwise dead to the pointer.
 */
export function ProgressRows({
  rows,
  highlight = null,
}: {
  rows: ProgressRow[];
  highlight?: RowHighlight;
}) {
  const navigate = useNavigate();

  if (rows.length === 0) {
    return (
      <Text py={3} fontSize="sm" color="fg.muted">Nothing falls in this year.</Text>
    );
  }

  return (
    <Table.Root size="sm" interactive>
      <Table.Header>
        <Table.Row bg="transparent">
          <Table.ColumnHeader w="6" p={0} />
          <Table.ColumnHeader>
            Activity
          </Table.ColumnHeader>
          <Table.ColumnHeader
            w="64"
          >
            Status
          </Table.ColumnHeader>
          <Table.ColumnHeader
            w="16"
            textAlign="right"
          >
            Steps
          </Table.ColumnHeader>
          <Table.ColumnHeader
            w="24"
            textAlign="right"
          >
            Due
          </Table.ColumnHeader>
        </Table.Row>
      </Table.Header>

      <Table.Body>
        {rows.map((row) => {
          const look = statusPresentation(row.status, row.overdue);
          const lit = matchesHighlight(row, highlight);
          const open = () => void navigate({
            to: "/activities/$personActivityId",
            params: { personActivityId: row.personActivityId },
          });
          return (
            <Table.Row
              key={row.personActivityId}
              cursor="pointer"
              onClick={open}
              // Dimming the rest rather than colouring the matches: the rows
              // keep the status colour they are read by, and the ones being
              // counted are the ones that stay legible.
              opacity={highlight !== null && !lit ? 0.35 : 1}
              bg={lit ? "bg.emphasized" : undefined}
              transition="opacity 0.12s, background-color 0.12s"
              // Reachable and operable without a pointer: the row is the
              // control, so it has to answer to the keyboard like one.
              tabIndex={0}
              role="link"
              aria-label={`${row.title} — ${statusSentenceText(row)}`}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  open();
                }
              }}
            >
              {/* The CELL is the block, rather than a box inside it. A cell
                  fills its row's height by definition, where a child has to
                  be told to — and `height: 100%` inside a table cell is the
                  one place that reliably does not work, which is why a
                  two-line row had a short square floating at the top of it.
                  Cells centre their content vertically already. */}
              <Table.Cell
                w="6"
                p={0}
                textAlign="center"
                colorPalette={look.tone}
                bg="colorPalette.subtle"
                color="colorPalette.fg"
                borderRightWidth="1px"
                borderRightColor="colorPalette.muted"
              >
                <Icon as={look.icon} boxSize={3.5} />
              </Table.Cell>

              <Table.Cell py={2.5}>
                <Text fontSize="sm" fontWeight="medium">{row.title}</Text>
                {!row.required ? (
                  <Text fontSize="xs" color="fg.muted">
                    {row.origin === "selfEnrolled" ? "Voluntary" : "Encouraged"}
                  </Text>
                ) : null}
              </Table.Cell>

              <Table.Cell py={2.5} w="64">
                <Text fontSize="xs" color="fg.muted">
                  <StatusSentence row={row} />
                </Text>
                {row.waitingOn ? (
                  <Text fontSize="xs" color="fg.muted">
                    Waiting on{" "}
                    <Text as="span" fontWeight="semibold" color="fg">{row.waitingOn}</Text>
                  </Text>
                ) : null}
              </Table.Cell>

              <Table.Cell py={2.5} w="16" textAlign="right">
                {row.stepsTotal > 0 ? (
                  <Text fontSize="xs" color="fg.muted" fontVariantNumeric="tabular-nums">
                    {row.stepsDone}/{row.stepsTotal}
                  </Text>
                ) : null}
              </Table.Cell>

              <Table.Cell py={2.5} w="24" textAlign="right">
                {row.overdue ? (
                  <Badge size="sm" colorPalette="orange" variant="subtle">
                    {formatDay(row.dueDay)}
                  </Badge>
                ) : (
                  <Text fontSize="xs" color="fg.muted" fontVariantNumeric="tabular-nums">
                    {formatDay(row.dueDay)}
                  </Text>
                )}
              </Table.Cell>
            </Table.Row>
          );
        })}
      </Table.Body>
    </Table.Root>
  );
}

/**
 * One year's activities as a row of squares, one per activity.
 *
 * The same squares as the matrix, at the same size and carrying the same
 * icons, so a reader who has learnt the scheme once reads it everywhere. The
 * shape of a year is legible before any reading, and each square names its
 * activity and its state on hover.
 */
export function ProgressBar({ rows }: { rows: ProgressRow[] }) {
  if (rows.length === 0) {
    return <Box h="6" borderWidth="1px" borderStyle="dashed" borderColor="border" />;
  }
  return (
    <HStack gap={1} w="full">
      {rows.map((row) => {
        const look = statusPresentation(row.status, row.overdue);
        return (
          <Tooltip
            key={row.personActivityId}
            content={
              <>
                <Text fontWeight="medium">{row.title}</Text>
                {/* Italic, so that the status reads as a note about the
                    activity rather than as a second line of its name. */}
                <Text fontStyle="italic">{statusSentenceText(row)}</Text>
              </>
            }
          >
            {/* Grows to fill the year rather than sitting in a clump at
                the left: the strip is a bar across the year, and a bar
                that stops a third of the way across reads as missing
                data. Square at its narrowest, wider when there is room. */}
            <Flex
              flex="1"
              minW="6"
              h="6"
              align="center"
              justify="center"
              colorPalette={look.tone}
              bg="colorPalette.subtle"
              color="colorPalette.fg"
              borderWidth="1px"
              borderColor="colorPalette.muted"
              cursor="default"
            >
              <Icon as={look.icon} boxSize={3} />
            </Flex>
          </Tooltip>
        );
      })}
    </HStack>
  );
}
